import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";
import WalletPaymentDialog from "../WalletPaymentDialog";

const PaymentRequestIndividualMessage = ({
  message,
  isOwn,
  currentUser,
  storedUserId,
  onViewOrder,
  onOpenPaymentDialog,
  getReadReceiptStatus,
  getCurrencySymbol,
  formattedMessage,
  wrappedOnOpenPaymentDialog,
  walletPaymentDialogOpen,
  handleCloseWalletPaymentDialog,
  handleSendManualPaymentRequest,
}) => {
  const currentUserId = currentUser?.userId ?? storedUserId;
  const currentUserIdStr = String(currentUserId || "");

  const isUserInInitiationFor = message.initiationFor && 
    Array.isArray(message.initiationFor) &&
    message.initiationFor.some((id) => String(id || "") === currentUserIdStr);

  if (!isUserInInitiationFor) {
    return null;
  }

  const messageForUserId = message.messageFor;
  const messageForUserIdStr = messageForUserId ? String(messageForUserId) : null;
  const isMessageForCurrentUser = messageForUserIdStr === currentUserIdStr;

  const orderData = message?.orderId;
  const orderBusinessId = orderData?.businessId?._id || orderData?.businessId;
  const orderLocationId = orderData?.locationId?._id || orderData?.locationId;

  let isCurrentUserOwnerOrTeamMemberForThisOrder = false;
  const employeeInfo = currentUser?.employee;
  if (employeeInfo) {
    const employeeBusinessId =
      employeeInfo?.businessId?._id || employeeInfo?.businessId;
    const isSameBusiness =
      employeeBusinessId &&
      orderBusinessId &&
      employeeBusinessId === orderBusinessId;

    let hasSameLocation = false;
    if (employeeInfo?.locations && Array.isArray(employeeInfo.locations)) {
      hasSameLocation = employeeInfo.locations.some((loc) => {
        const locId = loc?._id || loc;
        return orderLocationId && locId === orderLocationId;
      });
    }

    isCurrentUserOwnerOrTeamMemberForThisOrder = Boolean(
      isSameBusiness || hasSameLocation
    );
  }

  if (isCurrentUserOwnerOrTeamMemberForThisOrder && !isUserInInitiationFor) {
    return null;
  }

  const orderId = message?.orderId;
  const userOrders = orderId?.groupOrder?.userOrders || [];
  const currentUserOrder = userOrders.find((userOrder) => {
    const userId = userOrder.userId?._id || userOrder.userId;
    return userId === currentUserId;
  });

  const customMessageText = formattedMessage.text;

  const remainingAmount = (currentUserOrder?.remainingAmount || 0);
  const hasAmountToPay = (currentUserOrder?.amountToPay || 0) > 0;
  const hasRemainingAmount = remainingAmount > 0;
  const messageAmount = message?.amount || 0;
  const paymentStatus = currentUserOrder?.paymentStatus;
  const amountPaid = currentUserOrder?.amountPaid || 0;
  
  const paymentFlow = currentUserOrder?.paymentFlow;
  const upfrontAmountAtInitiation = paymentFlow?.upfrontAmountAtInitiation || 0;
  
  const isUpfrontPaid = upfrontAmountAtInitiation > 0 
    ? amountPaid >= upfrontAmountAtInitiation
    : (paymentStatus === "upfront_paid" || paymentStatus === "paid");
  
  const hasOutstandingUpfront = messageAmount > 0 
    ? amountPaid < upfrontAmountAtInitiation
    : (!isUpfrontPaid && (
        paymentStatus !== "paid" && paymentStatus !== "upfront_paid"
      ));
  
  let shouldShowPayButton = false;
  if (message?.buttonDisabled !== true && !isCurrentUserOwnerOrTeamMemberForThisOrder) {
    shouldShowPayButton =
      (!messageForUserIdStr || isMessageForCurrentUser) &&
      (hasOutstandingUpfront ||
        (hasRemainingAmount && paymentStatus !== "paid" && paymentStatus !== "upfront_paid") ||
        (hasAmountToPay && paymentStatus !== "paid" && paymentStatus !== "upfront_paid"));
  }

  return (
    <>
      <MessageBubbleContainer isOwn={isOwn}>
        {!isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "#374151",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              fontWeight: 600,
              mb: 0.5,
              alignSelf: "flex-start",
            }}
          >
            {message.senderId?.fullName || message.senderId?.userName || "user"}
          </Typography>
        )}

        <MessageContent
          isOwn={isOwn}
          isTemp={message.isTemp}
          isOrder={false}
          isPayment={true}
          isRequest={false}
          idDraft={false}
          isDisabled={false}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              minWidth: "280px",
              maxWidth: "380px",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            <Box
              sx={{
                color: isOwn ? "#FFFFFF" : "#000000",
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              {customMessageText || "Individual payment request"}
            </Box>

            {shouldShowPayButton && onViewOrder && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    wrappedOnOpenPaymentDialog(message);
                  }}
                  sx={{
                    backgroundColor: isOwn ? "#ffffff" : "#7C3AED",
                    color: isOwn ? "#7C3AED" : "#ffffff",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: isOwn ? "#f3f4f6" : "#6d28d9",
                    },
                  }}
                >
                  {(() => {
                    const messageAmount = message?.amount || 0;
                    const amountToDisplay = messageAmount > 0 
                      ? messageAmount 
                      : (currentUserOrder?.remainingAmount || 
                         ((currentUserOrder?.amountToPay || 0) - (currentUserOrder?.amountPaid || 0)) || 
                         0);
                    
                    const currencyCode = orderId?.currency || "USD";
                    const currencySymbol = getCurrencySymbol(currencyCode);
                    return amountToDisplay > 0
                      ? `pay ${currencySymbol}${amountToDisplay.toFixed(2)}`
                      : "pay now";
                  })()}
                </Button>
              </Box>
            )}
          </Box>
        </MessageContent>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isOwn ? "flex-end" : "flex-start",
            gap: 1,
            mt: 1,
            width: "100%",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
            }}
          >
            {message.timestamp}
          </Typography>

          {isOwn && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(0,0,0,0.6)",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              {message.isTemp ? (
                <AccessTime fontSize="inherit" />
              ) : (
                getReadReceiptStatus() || "✓"
              )}
            </Typography>
          )}
        </Box>
      </MessageBubbleContainer>

      <WalletPaymentDialog
        open={walletPaymentDialogOpen}
        onClose={handleCloseWalletPaymentDialog}
        onSendManualPayment={handleSendManualPaymentRequest}
      />
    </>
  );
};

export default PaymentRequestIndividualMessage;
